import { VuexModule, Mutation, Action, Module } from 'vuex-module-decorators';
import {
  AuthenticationMutation,
  AdminUserMutations,
  VeterinarianMutation,
  VeterinarianOfficeUsersMutations,
  CustomersMutations,
  RegisteredTagMutations
} from '@/constants/mutation.constants';
import { LoginResponseModel } from '@/models/DTO/authentication/login-response.model';
import { Inject } from 'inversify-props';
import { LoginModel } from '@/models/DTO/authentication/login.model';
import { AuthenticationGetters } from '@/constants/getters.constants';
import { AuthenticationActions } from '@/constants/actions.constants';
import { ForgotPasswordModel } from '@/models/DTO/authentication/forgot-password.model';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { SetPasswordModel } from '@/models/DTO/authentication/set-password.model';
import { ResetPasswordModel } from '@/models/DTO/authentication/reset-password.model';
import { ChangePasswordModel } from '@/models/DTO/authentication/change-password.model';
import { RegisterAdminModel } from '@/models/DTO/authentication/register-admin.model';
import { RegisterVeterinarianModel } from '@/models/DTO/authentication/register-veterinarian.model';
import { JWT } from '@/models/DTO/authentication/jwt.model';
import moment, { Moment } from 'moment';
import { RequestChangeEmailModel } from '@/models/DTO/authentication/request-change-email.model';
import { ConfirmChangeEmailModel } from '@/models/DTO/authentication/confirm-change-email.model';
import { AuthenticationService } from '@/services/authentication.service';
import { CustomerLoginTokenRequestModel } from '@/models/DTO/authentication/customer-login-token-request.model';

export const AUTHENTICATION_MODULE_NAME = 'authentication';

// TODO: create default state factory function

@Module({
  namespaced: true,
  name: AUTHENTICATION_MODULE_NAME
})
export default class AuthenticationModule extends VuexModule {
  // #region Services
  @Inject()
  private readonly _authenticationService!: AuthenticationService;
  // #endregion

  // #region State
  public isLoginComponent = true;

  // Show for full page loading event
  public showLoading = false;

  // Show on button only
  public showButtonLoading = false;

  public acceptedTermsToViewCustomerData = false;
  public tagHasMatch = true;
  public ownerAuthentication = false;
  public errorResponse: BaseAPIErrorModel | null = null;
  public loginResponse: LoginResponseModel | null = null;
  public forgotPasswordSuccess: boolean | null = null;
  public setPasswordSuccess: boolean | null = null;
  public resetPasswordSuccess: boolean | null = null;
  public changePasswordSuccess: boolean | null = null;
  public registerAdminSuccess: boolean | null = null;
  public registerVeterinarianSuccess: boolean | null = null;
  public requestChangeEmailSuccess: boolean | null = null;
  public confirmChangeEmailSuccess: boolean | null = null;
  public confirmChangeEmailIsCustomer: boolean | null = null;
  public customerLoginTokenRequestSuccess: boolean | null;
  // #endregion

  // #region Getters
  public get [AuthenticationGetters.hasErrorMessages](): boolean {
    return this.context.getters[AuthenticationGetters.errorMessages].length > 0;
  }

  public get [AuthenticationGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }

  public get [AuthenticationGetters.jwtData](): JWT | null {
    return this.loginResponse?.token
      ? JSON.parse(atob(this.loginResponse.token.split('.')[1]))
      : null;
  }

  public get [AuthenticationGetters.jwtExpires](): Moment | null {
    const exp = this[AuthenticationGetters.jwtData]?.exp;

    if (!exp) {
      return null;
    }

    const expires = moment.unix(exp).utc();

    if (!expires.isValid()) {
      return null;
    }

    return expires.clone();
  }

  public get [AuthenticationGetters.jwtEmail](): string {
    return this.context.getters[AuthenticationGetters.jwtData]?.email;
  }

  public get [AuthenticationGetters.jwtRoles](): string[] {
    const roles =
      this.context.getters[AuthenticationGetters.jwtData]
        ?.role /* note ".role" here */ ?? [];
    return typeof roles === 'string' ? [roles] : roles;
  }

  public get [AuthenticationGetters.jwtVeterinarianOfficeId](): string {
    return this.context.getters[AuthenticationGetters.jwtData]
      ?.veterinarianOfficeId;
  }

  public get [AuthenticationGetters.hasAcceptedTerms](): boolean {
    return this.acceptedTermsToViewCustomerData;
  }

  public get [AuthenticationGetters.hasTagGottaMatch](): boolean {
    return this.tagHasMatch;
  }

  public get [AuthenticationGetters.hasOwnerAuthenticated](): boolean {
    return this.ownerAuthentication;
  }
  // #endregion

  // #region Action
  @Action
  public async [AuthenticationActions.changeComponentStateAction](
    payload: boolean
  ): Promise<void> {
    this.context.commit(AuthenticationMutation.component, payload);
  }

  @Action
  public async [AuthenticationActions.loginAction](payload: LoginModel) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any login errors
      this.context.commit(AuthenticationMutation.error, null);

      let resp: LoginResponseModel | null = null;

      if (payload.isCustomer) {
        resp = await this._authenticationService.customerLogin({
          ...payload
        });
      } else {
        resp = await this._authenticationService.login({
          ...payload
        });
      }

      // success
      this.context.commit(AuthenticationMutation.login, { ...resp });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.customerLoginTokenRequestAction](
    payload: CustomerLoginTokenRequestModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any login errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.customerLoginTokenRequest({
        ...payload
      });

      // success
      this.context.commit(
        AuthenticationMutation.customerLoginTokenRequestMutation,
        true
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.refreshTokenAction]() {
    try {
      const resp = await this._authenticationService.refreshToken();

      // success same as login
      this.context.commit(AuthenticationMutation.login, { ...resp });
    } catch (error) {
      if (error?.response?.data) {
        console.log(error.response.data);

        const e = new BaseAPIErrorModel(error.response.data);
        this.context.commit(AuthenticationMutation.error, e);
      } else {
        console.log(error);
      }
    }
  }

  /**
   * This should only be called by the logout action.
   * It removes an HTTPS cookie for the refresh token.
   *
   * @param {RevokeTokenModel} [payload]
   * @memberof AuthenticationModule
   */
  @Action /* yes, private! */
  private async [AuthenticationActions._revokeRefreshTokenAction]() {
    try {
      // remove any login errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.revokeToken();
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    }
  }

  @Action
  public async [AuthenticationActions.logoutAction]() {
    try {
      this.context.commit(AuthenticationMutation.loading, true);

      // remove any login errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.logout();

      this.context.commit(
        `adminUser/${AdminUserMutations.resetStateMutation}`,
        null,
        {
          root: true
        }
      );
      this.context.commit(
        `veterinarian/${VeterinarianMutation.resetStateMutation}`,
        null,
        {
          root: true
        }
      );
      this.context.commit(
        `veterinarianUser/${VeterinarianOfficeUsersMutations.resetStateMutation}`,
        null,
        {
          root: true
        }
      );
      this.context.commit(
        `customers/${CustomersMutations.resetStateMutation}`,
        null,
        {
          root: true
        }
      );
      this.context.commit(
        `registeredTag/${RegisteredTagMutations.resetStateMutation}`,
        null,
        {
          root: true
        }
      );
      this.context.commit(AuthenticationMutation.logout);
    } catch (error) {
      console.log(error.response.data);
    } finally {
      this.context.commit(AuthenticationMutation.loading, false);
    }
  }

  @Action
  public async [AuthenticationActions.forgotPasswordAction](
    payload: ForgotPasswordModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.forgotPassword({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.forgotPassword, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.setPasswordAction](
    payload: SetPasswordModel
  ) {
    try {
      // Loading is not needed for this operation
      // this.context.commit(AuthenticationMutation.loading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.setPassword({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.setPassword, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.loading, false);
    }
  }

  @Action
  public async [AuthenticationActions.resetPasswordAction](
    payload: ResetPasswordModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.resetPassword({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.resetPassword, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.changePasswordAction](
    payload: ChangePasswordModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.changePassword({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.changePassword, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.registerAdminAction](
    payload: RegisterAdminModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.registerAdmin({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.registerAdmin, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.registerVeterinarianAction](
    payload: RegisterVeterinarianModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.registerVeterinarian({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.registerVeterinarian, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.requestChangeEmailAction](
    payload: RequestChangeEmailModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.buttonLoading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      await this._authenticationService.requestChangeEmail({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.requestChangeEmail, true);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.buttonLoading, false);
    }
  }

  @Action
  public async [AuthenticationActions.confirmChangeEmailAction](
    payload: ConfirmChangeEmailModel
  ) {
    try {
      this.context.commit(AuthenticationMutation.loading, true);

      // remove any errors
      this.context.commit(AuthenticationMutation.error, null);

      const {
        isCustomer
      } = await this._authenticationService.confirmChangeEmail({
        ...payload
      });

      // success
      this.context.commit(AuthenticationMutation.confirmChangeEmail, [
        true,
        isCustomer
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AuthenticationMutation.error, e);
    } finally {
      this.context.commit(AuthenticationMutation.loading, false);
    }
  }

  @Action
  public async [AuthenticationActions.acceptTermsAction](
    payload: boolean
  ): Promise<void> {
    this.context.commit(AuthenticationMutation.loading, true);

    await this._authenticationService.setAcceptedTerms();

    this.context.commit(AuthenticationMutation.acceptTerms, payload);
    this.context.commit(AuthenticationMutation.loading, false);
  }

  @Action
  public async [AuthenticationActions.tagResetMatchAction](): Promise<void> {
    return this.context.commit(AuthenticationMutation.tagMatch, true);
  }

  @Action
  public async [AuthenticationActions.tagMatchAction](
    payload: boolean
  ): Promise<void> {
    return this.context.commit(AuthenticationMutation.tagMatch, payload);
  }

  @Action
  public async [AuthenticationActions.resetOwnerAuthentication](): Promise<
    void
  > {
    return this.context.commit(AuthenticationMutation.authenticateOwner, false);
  }

  @Action
  public async [AuthenticationActions.authenticateOwner](
    payload: boolean
  ): Promise<void> {
    return this.context.commit(
      AuthenticationMutation.authenticateOwner,
      payload
    );
  }
  // #endregion

  // #region Mutation
  @Mutation
  private [AuthenticationMutation.loading](show: boolean) {
    this.showLoading = show;
  }

  @Mutation
  private [AuthenticationMutation.buttonLoading](show: boolean) {
    this.showButtonLoading = show;
  }

  @Mutation
  private [AuthenticationMutation.component](isLoginComponent: boolean) {
    this.isLoginComponent = isLoginComponent;
  }

  @Mutation
  private [AuthenticationMutation.error](errorModel: BaseAPIErrorModel | null) {
    this.errorResponse = errorModel;
  }

  @Mutation
  private [AuthenticationMutation.login](response: LoginResponseModel | null) {
    if (response === null) {
      this.loginResponse = response;
    } else {
      this.loginResponse = {
        ...response
      };
    }
  }

  @Mutation
  private [AuthenticationMutation.customerLoginTokenRequestMutation](
    data: boolean | null
  ) {
    this.customerLoginTokenRequestSuccess = data;
  }

  @Mutation
  private [AuthenticationMutation.logout]() {
    this.loginResponse = null;
  }

  @Mutation
  private [AuthenticationMutation.forgotPassword](response: boolean | null) {
    this.forgotPasswordSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.setPassword](response: boolean | null) {
    this.setPasswordSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.resetPassword](response: boolean | null) {
    this.resetPasswordSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.changePassword](response: boolean | null) {
    this.changePasswordSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.registerAdmin](response: boolean | null) {
    this.registerAdminSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.registerVeterinarian](
    response: boolean | null
  ) {
    this.registerVeterinarianSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.requestChangeEmail](
    response: boolean | null
  ) {
    this.requestChangeEmailSuccess = response;
  }

  @Mutation
  private [AuthenticationMutation.confirmChangeEmail](response: boolean[]) {
    this.confirmChangeEmailSuccess = response[0];
    this.confirmChangeEmailIsCustomer = response[1];
  }

  @Mutation
  private [AuthenticationMutation.acceptTerms](accepted: boolean) {
    this.acceptedTermsToViewCustomerData = accepted;
  }

  @Mutation
  private [AuthenticationMutation.tagMatch](hasMatch: boolean) {
    this.tagHasMatch = hasMatch;
  }

  @Mutation
  private [AuthenticationMutation.authenticateOwner](authenticated: boolean) {
    this.ownerAuthentication = authenticated;
  }
  // #endregion
}
